import { useMemo, useState } from 'react'
import { Button, Form, Modal } from 'react-bootstrap'
import { jsPDF } from 'jspdf'
import UbuntuBase64 from '../assets/fonts/UbuntuBase64'
import UbuntuBoldBase64 from '../assets/fonts/UbuntuBoldBase64'
import { fetchStoragePrintData } from './storagePrintData'

const toTrim = (value) => String(value ?? '').trim()
const toNum = (value, fallback = 0) => {
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}

const toBase64Payload = (asset) => {
  const str = String(asset || '')
  const idx = str.indexOf(',')
  return idx >= 0 ? str.slice(idx + 1) : str
}

const normalizeText = (value) =>
  toTrim(value)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')

const seasonLabel = (value) => {
  const text = normalizeText(value)
  if (text === '1' || text === 'letni' || text === 'leto') return 'Léto'
  if (text === '2' || text === 'zimni' || text === 'zima') return 'Zima'
  if (text === '3' || text === 'celorocni') return 'Celoroční'
  return toTrim(value) || '-'
}

const EAN_L = ['0001101', '0011001', '0010011', '0111101', '0100011', '0110001', '0101111', '0111011', '0110111', '0001011']
const EAN_G = ['0100111', '0110011', '0011011', '0100001', '0011101', '0111001', '0000101', '0010001', '0001001', '0010111']
const EAN_R = ['1110010', '1100110', '1101100', '1000010', '1011100', '1001110', '1010000', '1000100', '1001000', '1110100']
const EAN_PARITY = ['LLLLLL', 'LLGLGG', 'LLGGLG', 'LLGGGL', 'LGLLGG', 'LGGLLG', 'LGGGLL', 'LGLGLG', 'LGLGGL', 'LGGLGL']

const ean13Bits = (value) => {
  const ean = toTrim(value)
  if (!/^\d{13}$/.test(ean)) return ''
  const parity = EAN_PARITY[Number(ean[0])]
  let bits = '101'
  for (let index = 1; index <= 6; index += 1) {
    bits += parity[index - 1] === 'L' ? EAN_L[Number(ean[index])] : EAN_G[Number(ean[index])]
  }
  bits += '01010'
  for (let index = 7; index <= 12; index += 1) bits += EAN_R[Number(ean[index])]
  return `${bits}101`
}

const drawEan13 = (doc, value, x, y, width, height) => {
  const ean = toTrim(value)
  const bits = ean13Bits(ean)
  if (!bits) {
    doc.setFont('Ubuntu', 'bold')
    doc.setFontSize(9)
    doc.text(ean || '-', x, y + (height / 2))
    return
  }
  const moduleWidth = width / bits.length
  doc.setFillColor(0)
  bits.split('').forEach((bit, index) => {
    if (bit === '1') doc.rect(x + (index * moduleWidth), y, moduleWidth + 0.03, height, 'F')
  })
  doc.setFont('Ubuntu', 'normal')
  doc.setFontSize(6.5)
  doc.text(ean[0], x - 2.3, y + height + 2.5)
  doc.text(ean.slice(1, 7), x + (width * 0.28), y + height + 2.5, { align: 'center' })
  doc.text(ean.slice(7), x + (width * 0.76), y + height + 2.5, { align: 'center' })
}

const registerUbuntuFonts = (doc) => {
  doc.addFileToVFS('Ubuntu-Regular.ttf', toBase64Payload(UbuntuBase64))
  doc.addFont('Ubuntu-Regular.ttf', 'Ubuntu', 'normal')
  doc.addFileToVFS('Ubuntu-Bold.ttf', toBase64Payload(UbuntuBoldBase64))
  doc.addFont('Ubuntu-Bold.ttf', 'Ubuntu', 'bold')
}

const drawLabel = (doc, label, x, y, w, h) => {
  const innerPad = 6
  const innerLeft = x + innerPad
  const innerRight = x + w - innerPad
  const contentWidth = innerRight - innerLeft
  const contentHeight = 48
  const contentTop = y + ((h - contentHeight) / 2)
  const spz = toTrim(label.spz) || '-'
  const ean = toTrim(label.ean)
  const pozice = toTrim(label.pozice) || '-'
  const zakaznik = toTrim(label.firma) || toTrim(label.jmeno) || '-'
  const sezona = seasonLabel(label.sezona)

  // Rámeček štítku je dočasně vypnutý pro tisk bez orámování.
  // Pro opětovné zapnutí stačí odkomentovat následující 3 řádky.
  // doc.setDrawColor(0)
  // doc.setLineWidth(0.3)
  // doc.rect(x, y, w, h)

  const positionWidth = 24
  const positionHeight = 14
  const barcodeWidth = contentWidth - positionWidth - 10
  drawEan13(doc, ean, innerLeft + 1, contentTop, barcodeWidth, 10)

  doc.setDrawColor(0)
  doc.setLineWidth(0.7)
  doc.setLineDashPattern([0.7, 1.2], 0)
  doc.roundedRect(innerRight - positionWidth, contentTop - 0.5, positionWidth, positionHeight, 0.8, 0.8)
  doc.setLineDashPattern([], 0)
  doc.setFont('Ubuntu', 'normal')
  doc.setFontSize(18)
  doc.text(pozice, innerRight - (positionWidth / 2), contentTop + 9.5, { align: 'center' })

  doc.setFont('Ubuntu', 'bold')
  doc.setFontSize(28)
  doc.text(spz, innerLeft, contentTop + 26)

  doc.setLineWidth(0.25)
  doc.line(innerLeft, contentTop + 29, innerRight, contentTop + 29)

  doc.setFont('Ubuntu', 'bold')
  doc.setFontSize(8)
  doc.text(sezona, innerLeft, contentTop + 34)

  doc.setFontSize(15)
  const customerLines = doc.splitTextToSize(zakaznik, contentWidth)
  doc.text(customerLines.slice(0, 1), innerLeft, contentTop + 42)
}

const buildPdf = (label, labelCount, startPosition) => {
  const count = Math.max(1, Number(labelCount) || 1)
  const startSlot = Math.min(8, Math.max(1, Number(startPosition) || 1)) - 1
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
  registerUbuntuFonts(doc)

  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const contentWidth = pageWidth
  const contentHeight = pageHeight
  const cols = 2
  const rows = 4
  const labelWidth = contentWidth / cols
  const labelHeight = contentHeight / rows
  const labelsPerPage = cols * rows

  for (let i = 0; i < count; i += 1) {
    const sheetIndex = startSlot + i
    const pageIndex = Math.floor(sheetIndex / labelsPerPage)
    if (pageIndex > 0 && sheetIndex % labelsPerPage === 0) doc.addPage()
    const position = sheetIndex % labelsPerPage
    const col = position % cols
    const row = Math.floor(position / cols)
    const x = col * labelWidth
    const y = row * labelHeight
    const tireRows = Array.isArray(label?.pneuRows) ? label.pneuRows : []
    const tire = tireRows.length ? tireRows[i % tireRows.length] : {}
    drawLabel(doc, { ...label, ...tire }, x, y, labelWidth, labelHeight)
  }

  // Doporučení pro PDF viewer: tisk bez automatického škálování stránky.
  if (typeof doc.viewerPreferences === 'function') {
    doc.viewerPreferences({ PrintScaling: 'None' })
  }
  doc.autoPrint()
  const blobUrl = doc.output('bloburl')
  if (typeof window !== 'undefined') window.open(blobUrl, '_blank', 'noopener,noreferrer')
}

const StorageLabelsPrintTrigger = ({ zakazkaId, copies = 8, labelCount, renderButton, disabled = false }) => {
  const [showModal, setShowModal] = useState(false)
  const [startPosition, setStartPosition] = useState(1)
  const [isLoadingData, setIsLoadingData] = useState(false)
  const [printData, setPrintData] = useState(null)
  const [loadError, setLoadError] = useState('')

  const isReady = useMemo(() => toNum(zakazkaId, 0) > 0, [zakazkaId])

  const effectiveLabelCount = useMemo(() => {
    const fromProp = toNum(labelCount, 0)
    if (fromProp > 0) return fromProp
    const fromData = toNum(printData?.labelCount, 0)
    if (fromData > 0) return fromData
    return Math.max(1, toNum(copies, 8))
  }, [labelCount, printData?.labelCount, copies])

  const handleOpen = async (event) => {
    event?.stopPropagation?.()
    if (disabled || !isReady) return
    setShowModal(true)
    setLoadError('')
    setIsLoadingData(true)
    try {
      const loaded = await fetchStoragePrintData(zakazkaId)
      setPrintData(loaded)
    } catch (error) {
      setPrintData(null)
      setLoadError(error?.message || 'Nepodařilo se načíst data zakázky pro tisk štítků.')
    } finally {
      setIsLoadingData(false)
    }
  }

  const handleModalClose = () => {
    setShowModal(false)
    setLoadError('')
  }

  const handlePrint = () => {
    if (!printData || isLoadingData) return
    buildPdf(printData, effectiveLabelCount, startPosition)
    setShowModal(false)
  }

  const isPrintDisabled = disabled || !isReady || isLoadingData || !printData || !!loadError

  return (
    <>
      {typeof renderButton === 'function'
        ? renderButton({ onClick: handleOpen, disabled: disabled || !isReady })
        : (
          <button type="button" onClick={handleOpen} disabled={disabled || !isReady}>
            Tisk štítků
          </button>
        )}

      <Modal show={showModal} fullscreen="xl-down" onHide={handleModalClose} keyboard={true} centered size="sm">
        <Modal.Header closeButton className="border-0 p-4 pb-0">
          <Modal.Title className="text-primary lh-1">
            Tisk<br />
            <small className="text-muted fs-6">tisk štítků</small>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="small text-muted mb-3 px-2">
            Počet štítků k tisku: <span className="fs-5 fw-semibold">{effectiveLabelCount}</span>
          </p>
          {isLoadingData && <p className="small text-muted px-2 mb-2">Načítám data zakázky...</p>}
          {loadError && <p className="small text-danger px-2 mb-2">{loadError}</p>}
          <Form.Group>
            <Form.Label className="small text-muted mb-2 px-2">vyber pozici prvního štítku na archu:</Form.Label>
            <div
              className="border rounded p-4 mx-2"
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gridTemplateRows: 'repeat(4, 1fr)',
                gap: '3px',
                backgroundColor: 'var(--bs-tertiary-bg)',
              }}
            >
              {Array.from({ length: 8 }, (_, i) => i + 1).map((pos) => {
                const checked = startPosition === pos
                return (
                  <label
                    key={`pos-${pos}`}
                    className={`border rounded px-2 py-2 d-flex align-items-center justify-content-between ${checked ? 'border-primary bg-primary bg-opacity-10' : 'bg-body'}`}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    <span className="small text-muted">Pozice {pos}</span>
                    <Form.Check
                      type="radio"
                      name="label-start-position"
                      checked={checked}
                      onChange={() => setStartPosition(pos)}
                      className="mb-0"
                    />
                  </label>
                )
              })}
            </div>
          </Form.Group>
          <div className='mt-4 d-flex gap-1'>
           <Button className="ms-2" variant="primary" onClick={handlePrint} disabled={isPrintDisabled}>
            {isLoadingData ? 'Načítám...' : 'Tisk'}
          </Button>
          <Button className="ms-0" variant="secondary" onClick={handleModalClose}>Zavřít</Button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  )
}

export default StorageLabelsPrintTrigger
